import React from "react";
import Icon from "./Icon";

// I² Visual Identity v1 — IconTile (2026-04-29 evening, post-Hugeicons).
//
// User direction: "I need colorful icons that convey the story —
// something similar to iOS icons with glassmorphism." Decision #32:
// instead of importing a 3D-rendered icon library (3dicons isn't
// programmatically distributable; Fluent Emoji is too playful for
// enterprise), build the iOS-app-icon look as a composite primitive.
//
// Each tile is a squircle stack:
//   1. Saturated gradient background (per-category color story)
//   2. Glass sheen overlay (top-bright fading to bottom-dark — mimics
//      light catching real glass; classic iOS app icon trick)
//   3. Inner shadow at top (1px white, 30% opacity) — edge highlight
//   4. Outer drop shadow — floats above the surface
//   5. Hugeicons silhouette in white, centered
//
// Result: looks like an iOS home-screen app icon at 36-40px scale.
// "Story-conveying" via per-category color: Build = indigo (creation),
// Agents = cyan (intelligence), Marketplace = orange (commerce), etc.
//
// Color palette tuned to brand tokens (COLORS.accent / cyan / pink /
// orange / green / purple) plus a few additional warm/cool stops.

// Coordinated colorful palette (2026-04-29 night, post-monochrome-feedback).
// User: "maybe colorful but more matching colors." Reintroduced varied
// hues but with a disciplined palette where every entry sits at the
// SAME saturation + lightness band (Tailwind 400-500 stops). Result:
// every tile is a different hue (cohesive variety) but they all share
// the same tonal weight — no neon-versus-muddy mismatches that read
// as "candy store."
//
// All gradients use the same direction (135deg) and 2-stop range so
// the tile family looks designed, not random.
const TILE_GRADIENTS = {
  // Build family — purple/indigo/fuchsia (creation, primary activity)
  builder:     "linear-gradient(135deg, #8B5CF6 0%, #A855F7 100%)", // violet → purple
  agents:      "linear-gradient(135deg, #4F46E5 0%, #3B82F6 100%)", // indigo → blue (intelligence)
  marketplace: "linear-gradient(135deg, #D946EF 0%, #EC4899 100%)", // fuchsia → pink (discovery / commerce)
  skills:      "linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)", // amber → gold (sparkle / superpowers)

  // Operate family — broadcast / time / outbound
  channels:    "linear-gradient(135deg, #06B6D4 0%, #14B8A6 100%)", // cyan → teal (broadcast)
  campaigns:   "linear-gradient(135deg, #F43F5E 0%, #FB7185 100%)", // rose → coral (outbound — softer than amber/red)
  schedules:   "linear-gradient(135deg, #10B981 0%, #34D399 100%)", // emerald → light emerald (time / calm)

  // Observe family — analytical depth
  supervisor:  "linear-gradient(135deg, #7C3AED 0%, #6366F1 100%)", // violet → indigo (oversight)
  analytics:   "linear-gradient(135deg, #3B82F6 0%, #0EA5E9 100%)", // blue → sky (data)
  observability: "linear-gradient(135deg, #14B8A6 0%, #06B6D4 100%)", // teal → cyan (traces / pulse)

  // Settings — neutral authority
  admin:       "linear-gradient(135deg, #64748B 0%, #475569 100%)", // slate

  // Default fallback (matches Builder)
  default:     "linear-gradient(135deg, #8B5CF6 0%, #A855F7 100%)",
};

/**
 * <IconTile />
 *
 * @param {string}        category      — semantic category from TILE_GRADIENTS (builder/agents/...)
 * @param {string}        iconName      — semantic key passed to <Icon>
 * @param {number}        size          — outer tile px (default 36)
 * @param {number}        iconSize      — inner symbol px (default 0.6 * size)
 * @param {boolean}       active        — active state: brighter, slight scale, glow
 * @param {string}        ariaLabel     — accessibility label
 * @param {object}        style         — extra style overrides
 */
export default function IconTile({
  category,
  iconName,
  size = 36,
  iconSize,
  active = false,
  ariaLabel,
  style,
  ...rest
}) {
  const gradient = TILE_GRADIENTS[category] || TILE_GRADIENTS.default;
  const innerSize = iconSize ?? Math.round(size * 0.58);
  // True iOS squircle uses superellipse math; rounded-square at ~28% of
  // size is the visual approximation that holds at 36-40px.
  const radius = Math.round(size * 0.28);

  return (
    <div
      aria-label={ariaLabel}
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: gradient,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        flexShrink: 0,
        // Outer drop shadow (depth) + 1px inner-top highlight (edge gloss)
        boxShadow: active
          ? "0 6px 20px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,0.45), 0 0 0 2px rgba(255,255,255,0.20)"
          : "0 4px 12px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.32)",
        transform: active ? "scale(1.04)" : "scale(1)",
        transition: "transform 0.18s ease, box-shadow 0.18s ease",
        ...style,
      }}
      {...rest}
    >
      {/* Glass sheen overlay — top half catches "light," bottom dims
          slightly. Classic iOS app icon technique. */}
      <div style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(180deg, rgba(255,255,255,0.32) 0%, rgba(255,255,255,0.06) 45%, rgba(0,0,0,0.10) 100%)",
        pointerEvents: "none",
      }} />
      {/* Inner symbol — white-ish for max contrast against any gradient */}
      <div style={{ position: "relative", zIndex: 1, lineHeight: 0 }}>
        <Icon name={iconName} size={innerSize} color="#FAFAF7" />
      </div>
    </div>
  );
}

export { TILE_GRADIENTS };
